import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  intakeApiClient,
  useConvertIntakeToMatterMutation,
} from '../services/api/intakeService';
import { mattersApiClient } from '../services/api/mattersService';
import { DetailedMatter } from '../services/types/client-intake-types';
import { ManualQualificationEnum } from '../services/types/intake-types';

interface UseConvertToMatterProps {
  intakeId: string | null;
  matterId: string | null;
  matterStatus?: string | null;
  leadSource?: string;
  referralSource?: string;
  manualQualification?: ManualQualificationEnum | null;
  intakeStatus?: string;
  hasLinkedLead?: boolean;
}

interface LeadNotificationResult {
  notified: boolean;
  actions: string[];
  error: string | null;
}

const ACTION_PAST_TENSE: Record<string, string> = {
  evaluate: 'evaluated',
  accept: 'accepted',
  decline: 'declined',
};

interface UseConvertToMatterReturn {
  canConvert: boolean;
  isConverting: boolean;
  handleConvertToMatter: () => Promise<DetailedMatter | undefined>;
  isLeadSourceMissing: boolean;
  isReferralSourceMissing: boolean;
  isRejectedOrUnqualified: boolean;
  hasLinkedLead: boolean;
}

export const useConvertToMatter = ({
  intakeId,
  matterId,
  matterStatus,
  leadSource,
  referralSource,
  manualQualification,
  intakeStatus,
  hasLinkedLead = false,
}: UseConvertToMatterProps): UseConvertToMatterReturn => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [convertToMatter, { isLoading: isConverting }] =
    useConvertIntakeToMatterMutation();

  // A matter is "promoted" when it exists and has moved past INTAKE status.
  // Matters in INTAKE status were auto-created and haven't been fully activated yet.
  const hasMatter = Boolean(matterId && matterStatus !== 'Intake');
  const isLeadSourceMissing = !leadSource || leadSource.trim() === '';
  const isReferralSourceMissing = !referralSource || referralSource.trim() === '';
  const isRejectedOrUnqualified =
    manualQualification === ManualQualificationEnum.UNQUALIFIED ||
    intakeStatus === 'Rejected';
  const canConvert = Boolean(intakeId) && !isLeadSourceMissing && !isReferralSourceMissing;

  const handleConvertToMatter = useCallback(async (): Promise<
    DetailedMatter | undefined
  > => {
    if (!intakeId) {
      toast.error('No intake ID available');
      return undefined;
    }

    if (isLeadSourceMissing) {
      toast.error('Lead Source is required to create a matter');
      return undefined;
    }

    if (isReferralSourceMissing) {
      toast.error('Referral Source is required to create a matter');
      return undefined;
    }

    try {
      const matter = await convertToMatter(intakeId).unwrap();

      // Manually invalidate the matters cache in mattersApiClient
      dispatch(
        mattersApiClient.util.invalidateTags([
          { type: 'Matters' },
          { type: 'Matter', id: matter.id },
        ]),
      );

      if (hasMatter) {
        toast.success('Matter synced successfully!');
      } else {
        const actionLabel = matterStatus === 'Intake' ? 'Matter activated' : 'Matter created';
        toast.success(`${actionLabel} successfully!`, {
          duration: 5000,
          action: {
            label: 'View Matter',
            onClick: () => navigate(`/matters/${matter.id}`),
          },
        });
      }

      // Show Lexamica notification result toast
      const notification = (matter as DetailedMatter & { lead_notification?: LeadNotificationResult })
        .lead_notification;
      if (notification) {
        if (notification.error) {
          toast.error(
            `Lexamica update failed: ${notification.error}. Lead status was rolled back.`,
            { duration: 8000 },
          );
        } else if (notification.notified && notification.actions.length > 0) {
          const actionList = notification.actions
            .map((a) => ACTION_PAST_TENSE[a] ?? a)
            .join(', ');
          toast.success(`Case ${actionList} in Lexamica`, { duration: 5000 });
        }
      }

      return matter;
    } catch (error: any) {
      console.error('Failed to convert intake to matter:', error);

      if (error?.status === 409) {
        // Check if the response includes the existing matter data
        // This happens when another user created the matter while we were processing
        const existingMatter = error?.data?.matter;
        if (existingMatter?.id) {
          toast.info(
            'A matter was already created for this intake by another user.',
            {
              duration: 8000,
              action: {
                label: 'View Matter',
                onClick: () => navigate(`/matters/${existingMatter.id}`),
              },
            },
          );
          // Invalidate caches to refresh the UI with the existing matter
          dispatch(
            mattersApiClient.util.invalidateTags([
              { type: 'Matters' },
              { type: 'Matter', id: existingMatter.id },
            ]),
          );
          // Also invalidate intake cache so the UI reflects that a matter now exists
          dispatch(
            intakeApiClient.util.invalidateTags([
              { type: 'Intake', id: intakeId },
              'Intakes',
            ]),
          );
          return existingMatter;
        }
        // Fallback for legacy 409 responses without matter data
        toast.success('Matter is already up-to-date');
        return undefined;
      }

      const genericCreateMsg = 'Failed to create matter. Please try again.';
      const genericSyncMsg = 'Failed to sync matter. Please try again.';

      if (error?.data?.error) {
        toast.error(error.data.error);
      } else {
        toast.error(hasMatter ? genericSyncMsg : genericCreateMsg);
      }

      throw error;
    }
  }, [
    intakeId,
    hasMatter,
    matterStatus,
    convertToMatter,
    navigate,
    dispatch,
    isLeadSourceMissing,
    isReferralSourceMissing,
  ]);

  return {
    canConvert,
    isConverting,
    handleConvertToMatter,
    isLeadSourceMissing,
    isReferralSourceMissing,
    isRejectedOrUnqualified,
    hasLinkedLead,
  };
};
